import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import CheckIcon from '@mui/icons-material/Check';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { fromSubscription, useAppDispatch } from '../../../../store';
import { LoadingProgress } from '../../../../layout';
import { Panel } from '../../../../components/common/Panel';
import { TruePlatformWelcome } from './TruePlatformWelcome';
import { sendTrackingData } from '../../../../utils/utils';
import { CustomLink } from '../../../../components';
import { CUSTOMER_PRIVACY_POLICY, CUSTOMER_TERM_OF_USED } from '../../../../utils/Constants';

const FEATURES = [
  {
    key: 'ai_agents',
    nameId: 'page.true.platform.upgrade.feature.ai.agents',
    descId: 'page.true.platform.upgrade.feature.ai.agents.desc',
  },
  {
    key: 'cloud_execution',
    nameId: 'page.true.platform.upgrade.feature.cloud.execution',
    descId: 'page.true.platform.upgrade.feature.cloud.execution.desc',
  },
  {
    key: 'manual_testing',
    nameId: 'page.true.platform.upgrade.feature.manual.testing',
    descId: 'page.true.platform.upgrade.feature.manual.testing.desc',
  },
  {
    key: 'test_management',
    nameId: 'page.true.platform.upgrade.feature.test.management',
    descId: 'page.true.platform.upgrade.feature.test.management.desc',
  },
  {
    key: 'reporting',
    nameId: 'page.true.platform.upgrade.feature.reporting',
    descId: 'page.true.platform.upgrade.feature.reporting.desc',
  },
];

const useStyles = makeStyles(theme => ({
  column: {
    padding: theme.spacing(6),
    flex: 1,
  },
  sectionLabel: {
    fontSize: theme.spacing(1.5),
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: '#5959EB',
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionLabelDivider: {
    flex: 1,
    height: 1,
    backgroundColor: '#5959EB',
    opacity: 0.4,
  },
  mainTitle: {
    fontWeight: 800,
    fontSize: theme.spacing(3.5),
    color: '#1A1A3E',
    lineHeight: 1.25,
    marginBottom: theme.spacing(3),
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    margin: theme.spacing(1.5, 0),
    backgroundColor: '#fafaff',
    border: '1px solid #EEF0F9',
    borderRadius: theme.spacing(1),
  },
  checkCircle: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    borderRadius: '50%',
    backgroundColor: '#eeeeff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: theme.spacing(2, 2),
    marginLeft: theme.spacing(2),
  },
  checkIcon: {
    color: '#393995',
    fontSize: theme.spacing(2),
    fontWeight: theme.typography.fontWeightBold,
  },
  featureName: {
    fontWeight: 700,
    fontSize: theme.spacing(2),
    color: '#1A1A3E',
  },
  featureDesc: {
    fontSize: 12,
    color: '#6B6B8A',
    marginTop: 2,
  },
  rightTitle: {
    fontWeight: 800,
    fontSize: theme.spacing(3.5),
    color: '#1A1A3E',
    lineHeight: 1.25,
    marginBottom: theme.spacing(1),
  },
  rightSubtitle: {
    fontWeight: 700,
    fontSize: theme.spacing(2),
    color: '#1A1A3E',
    marginBottom: theme.spacing(2.5),
  },
  guaranteeBox: {
    backgroundColor: '#eef2ff',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2.5),
  },
  guaranteeTitle: {
    fontWeight: 700,
    fontSize: theme.spacing(2),
    color: '#5959EB',
    marginBottom: theme.spacing(0.5),
  },
  guaranteeDesc: {
    fontSize: theme.spacing(1.75),
    color: '#4A4A6A',
  },
  guaranteeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  shieldIcon: {
    color: '#5959EB',
    marginTop: 2,
    flexShrink: 0,
  },
  renewalInfoBox: {
    border: '1px solid #E0E3EF',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2.5),
    backgroundColor: '#f5f5f7',
  },
  renewalRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  renewalIcon: {
    color: '#6B6B8A',
    fontSize: theme.spacing(2.25),
    flexShrink: 0,
  },
  renewalLabel: {
    fontSize: theme.spacing(1.875),
    color: '#6B6B8A',
  },
  renewalValue: {
    fontWeight: 700,
    fontSize: theme.spacing(1.875),
    color: '#1A1A3E',
  },
  checkboxContainer: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(1),
  },
  checkboxLabel: {
    color: '#1A1A3E',
    lineHeight: 1.5,
  },
  upgradeButton: {
    width: '100%',
    fontWeight: 700,
    fontSize: theme.spacing(2),
    padding: theme.spacing(1.75, 3),
    borderRadius: theme.spacing(0.75),
    textTransform: 'none' as const,
    backgroundColor: '#5959EB',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#43509B',
    },
    '&:disabled': {
      backgroundColor: '#C4CAEC',
      color: '#fff',
    },
  },
  footnote: {
    textAlign: 'center' as const,
    fontSize: theme.spacing(1.75),
    color: '#6B6B8A',
    marginTop: theme.spacing(1.5),
  },
}));

export interface TruePlatformUpgradeProps {
  accountId: number;
  renewalDate?: Date;
}

const formatRenewalDate = (date: Date, locale: string): string => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString(locale, options);
};

export const TruePlatformUpgrade = ({ accountId, renewalDate }: TruePlatformUpgradeProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isUpgrading = useSelector(fromSubscription.selectLoading);
  const trueUpgradeInfo = useSelector(fromSubscription.selectTrueUpgradeInfo);
  const [checked, setChecked] = useState(false);

  const isUpgradeAtRenewal = renewalDate !== undefined;

  useEffect(() => {
    dispatch(fromSubscription.doGetTruePlatformUpgradeInfo());
  }, []);

  if (trueUpgradeInfo?.reserved) {
    return (
      <TruePlatformWelcome
        renewalDate={trueUpgradeInfo.renewalDate}
      />
    );
  }

  const handleActivate = () => {
    if (isUpgradeAtRenewal) {
      dispatch(fromSubscription.doReserveUpgradeToTruePlatform());
    } else {
      dispatch(fromSubscription.doUpgradeToTruePlatform());
    }

    sendTrackingData(
      'true_upgrade_click',
      accountId,
      undefined,
      {
        page: 'subscription_management',
        upgradeButtonName: isUpgradeAtRenewal ? 'Upgrade at Next Renewal' : 'Upgrade Immediately',
      },
    );
  };

  return (
    <>
      <Panel
        id="true-platform-upgrade-panel"
        sx={theme => ({
          margin: theme.spacing(4, 0),
        })}
        border
      >
        <Box
          id="true-platform-upgrade-container"
          display="flex"
          flexDirection="row"
          height="100%"
          sx={theme => ({
            [theme.breakpoints.down('md')]: {
              flexDirection: 'column',
            },
          })}
        >
          {/* Left column */}
          <Box className={classes.column}>
            <Box className={classes.sectionLabel}>
              <FormattedMessage
                id={isUpgradeAtRenewal
                  ? 'page.true.platform.upgrade.renewal.section.label'
                  : 'page.true.platform.upgrade.immediate.section.label'}
              />
              <Box className={classes.sectionLabelDivider} />
            </Box>

            <Typography className={classes.mainTitle}>
              <FormattedMessage
                id={isUpgradeAtRenewal
                  ? 'page.true.platform.upgrade.renewal.title'
                  : 'page.true.platform.upgrade.immediate.title'}
              />
            </Typography>

            {/* Feature list */}
            {FEATURES.map(feature => (
              <Box key={feature.key} className={classes.featureItem}>
                <Box className={classes.checkCircle}>
                  <CheckIcon className={classes.checkIcon} />
                </Box>
                <Box>
                  <Typography className={classes.featureName}>
                    <FormattedMessage id={feature.nameId} />
                  </Typography>
                  <Typography className={classes.featureDesc}>
                    <FormattedMessage id={feature.descId} />
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Divider
            id="true-platform-upgrade-divider"
            orientation="vertical"
            sx={{
              width: '1px',
              backgroundColor: '#fafafa',
              flexDirection: 'column',
            }}
            flexItem
          />
          {/* Right column */}
          <Box
            id="true-platform-upgrade-right-panel"
            key="true-platform-upgrade-right-panel"
            className={classes.column}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box
              id="true-platform-upgrade-right-panel-up"
              key="true-platform-upgrade-right-panel-up"
              pt={3}
            >
              <Typography className={classes.rightTitle}>
                <FormattedMessage
                  id={isUpgradeAtRenewal
                    ? 'page.true.platform.upgrade.renewal.right.title'
                    : 'page.true.platform.upgrade.immediate.right.title'}
                />
              </Typography>
              <Typography className={classes.rightSubtitle}>
                <FormattedMessage id="page.true.platform.upgrade.right.subtitle" />
              </Typography>

              {/* Guarantee box */}
              <Box className={classes.guaranteeBox}>
                <Box className={classes.guaranteeRow}>
                  <SecurityOutlinedIcon className={classes.shieldIcon} />
                  <Box>
                    <Typography className={classes.guaranteeTitle}>
                      <FormattedMessage id="page.true.platform.upgrade.guarantee.title" />
                    </Typography>
                    <Typography className={classes.guaranteeDesc}>
                      <FormattedMessage id="page.true.platform.upgrade.guarantee.desc" />
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Renewal info box (only when renewalDate is defined) */}
              {isUpgradeAtRenewal && renewalDate && (
                <Box className={classes.renewalInfoBox}>
                  <Box className={classes.renewalRow}>
                    <CalendarTodayOutlinedIcon className={classes.renewalIcon} />
                    <Typography className={classes.renewalLabel}>
                      <FormattedMessage id="page.true.platform.upgrade.renewal.date.label" />
                      {' '}
                      <span className={classes.renewalValue}>
                        {formatRenewalDate(renewalDate, intl.locale)}
                      </span>
                    </Typography>
                  </Box>
                  <Box className={classes.renewalRow}>
                    <CachedOutlinedIcon className={classes.renewalIcon} />
                    <Typography className={classes.renewalLabel}>
                      <FormattedMessage id="page.true.platform.upgrade.renewal.renews.as.label" />
                      {' '}
                      <span className={classes.renewalValue}>
                        <FormattedMessage id="page.true.platform.upgrade.renewal.renews.as.value" />
                      </span>
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Checkbox */}
              <Box className={classes.checkboxContainer}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={checked}
                      onChange={e => setChecked(e.target.checked)}
                      sx={{
                        color: '#5959EB',
                        '&.Mui-checked': { color: '#5959EB' },
                        alignSelf: 'flex-start',
                        marginTop: '-6px',
                      }}
                    />
                  )}
                  label={(
                    <Typography className={classes.checkboxLabel} fontSize={12}>
                      <FormattedMessage
                        id={isUpgradeAtRenewal
                          ? 'page.true.platform.upgrade.renewal.checkbox'
                          : 'page.true.platform.upgrade.immediate.checkbox'}
                        values={{
                          term: (
                            <CustomLink href={CUSTOMER_TERM_OF_USED}>
                              <FormattedMessage id="page.checkout.term.and.condition.term" />
                            </CustomLink>
                          ),
                          policy: (
                            <CustomLink href={CUSTOMER_PRIVACY_POLICY}>
                              <FormattedMessage id="page.checkout.term.and.condition.policy" />
                            </CustomLink>
                          ),
                        }}
                      />
                    </Typography>
                  )}
                  sx={{ alignItems: 'flex-start' }}
                />
              </Box>
            </Box>
            <Box
              id="true-platform-upgrade-right-panel-down"
              key="true-platform-upgrade-right-panel-down"
            >
              {/* CTA Button */}
              <Button
                id="true-platform-upgrade-btn"
                className={classes.upgradeButton}
                variant="contained"
                disableElevation
                disabled={!checked}
                onClick={handleActivate}
              >
                <FormattedMessage
                  id={isUpgradeAtRenewal
                    ? 'page.true.platform.upgrade.renewal.btn'
                    : 'page.true.platform.upgrade.immediate.btn'}
                />
                {' →'}
              </Button>

              {/* Footnote (immediate upgrade only) */}
              {!isUpgradeAtRenewal && (
                <Typography className={classes.footnote}>
                  <FormattedMessage id="page.true.platform.upgrade.immediate.footnote" />
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Panel>
      {isUpgrading && <LoadingProgress fullscreen />}
    </>
  );
};
